/**
 * Shared search behaviour for the condition/medication search boxes.
 *
 * Three things were wrong, all visible in the Search class logs:
 *
 * 1. react-search-autocomplete defaults to Fuse's threshold 0.6, which is
 *    loose enough to match almost anything. "uti" was resolving to Gout
 *    (351 logged searches), "toothache" to Athlete Foot, "std" to Stye.
 * 2. Patients search in lay language that never appears in a condition
 *    title. "Urinary Tract Infection" and "Dental Infection" both exist, so
 *    those two failures were pure vocabulary gaps, not missing services.
 * 3. Submitting the form navigated to searchResults[0] unconditionally, so
 *    a search for something we do not treat still sent the patient to
 *    whichever condition happened to score least badly. "diabetes" went to
 *    Scabies, "weight loss" to Hair Loss.
 *
 * Aliases below are deliberately limited to terms that are linguistically
 * equivalent to the condition (uti = urinary tract infection) or factual
 * brand/generic drug pairs (viagra = sildenafil). Clinical judgement calls
 * are NOT encoded here: "shingles", "cellulitis", "kidney infection" and
 * "ringworm" are all searched for but are left unmapped on purpose, because
 * deciding they are close enough to something we sell is a medical
 * decision, not a search one. They return no match instead.
 */

/** Titles carry stray capitals and trailing spaces, so compare normalised. */
export function normaliseTitle(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Patient vocabulary, keyed by the exact condition title in the Services
 * table. Every entry here was either seen in the search logs or is a direct
 * lay synonym of the title.
 */
export const CONDITION_ALIASES = {
  'urinary tract infection': [
    'uti', 'utis', 'u t i', 'bladder infection', 'urine infection',
    'water infection', 'cystitis', 'burning when i pee', 'painful urination',
    'burning pee', 'peeing burns',
  ],
  'dental infection': [
    'toothache', 'tooth ache', 'tooth pain', 'tooth infection', 'tooth',
    'teeth', 'dental pain', 'dental abscess', 'tooth abscess',
    'abscess tooth', 'gum infection', 'gum abscess', 'wisdom tooth',
    'infected tooth', 'dentist',
  ],
  'abscess (boil)': ['boil', 'boils', 'skin abscess', 'furuncle', 'carbuncle'],
  'pink eye': ['conjunctivitis', 'red eye', 'eye infection', 'pinkeye'],
  stye: ['eye stye', 'eyelid bump', 'sty'],
  'strep throat': ['strep', 'throat infection', 'strep test'],
  'sore throat treatment': ['sore throat', 'throat pain', 'scratchy throat'],
  'sinus infection': ['sinusitis', 'sinus', 'sinus pain'],
  'sinus & nasal congestion treatment': [
    'blocked nose', 'stuffy nose', 'nasal congestion', 'congestion',
  ],
  'cough & congestion treatment': ['cough', 'chest congestion', 'coughing'],
  'upper respiratory infection (uri)': [
    'chest cold', 'head cold', 'respiratory infection', 'uri',
  ],
  'cold and flu': ['flu', 'influenza', 'cold', 'fever', 'man flu'],
  bronchitis: ['chest infection'],
  asthma: ['wheezing', 'inhaler', 'wheeze'],
  allergy: ['allergies', 'hay fever', 'hayfever', 'allergic rhinitis', 'pollen'],
  'ear infection': ['earache', 'ear ache', 'ear pain', 'otitis', 'ear'],
  'acid reflux': ['heartburn', 'gerd', 'indigestion', 'reflux', 'acid'],
  'food poisoning': ['stomach bug', 'stomach flu', 'vomiting', 'diarrhea', 'diarrhoea'],
  'motion sickness': ['sea sickness', 'car sickness', 'travel sickness', 'nausea travel'],
  // 'thrush' on its own is left to Oral Thrush, which is how it is most
  // often meant; only the explicitly vaginal phrasings come here.
  'yeast infection': ['vaginal thrush', 'candida', 'vaginal yeast', 'yeast'],
  'bacterial vaginosis': ['bv', 'vaginal odor', 'vaginal odour', 'vaginal discharge'],
  'oral thrush': ['mouth thrush', 'white tongue', 'oral candida'],
  'genital herpes': ['herpes', 'hsv', 'hsv2', 'hsv 2', 'genital sores'],
  'cold sore': ['fever blister', 'lip sore', 'cold sores', 'hsv1', 'hsv 1'],
  chlamydia: ['chlamydia test'],
  gonorrhea: ['gonorrhoea', 'the clap', 'clap'],
  trichomoniasis: ['trich'],
  'erectile dysfunction': ['ed', 'impotence', 'erection problems', 'ed meds'],
  'premature ejaculation': ['pe', 'coming too quickly'],
  'athlete foot': ['athletes foot', "athlete's foot", 'tinea pedis', 'foot fungus', 'itchy feet'],
  'jock itch': ['tinea cruris', 'groin itch', 'itchy groin'],
  'toenail fungus': ['nail fungus', 'fungal nail', 'onychomycosis', 'thick toenails'],
  acne: ['pimples', 'zits', 'spots', 'breakout', 'acne treatment'],
  'eczema treatment': ['eczema', 'dermatitis', 'atopic dermatitis'],
  psoriasis: ['scaly skin', 'plaque psoriasis'],
  'rash treatment': ['rash', 'skin rash', 'hives', 'urticaria', 'itchy skin'],
  'poison ivy': ['poison oak', 'poison sumac'],
  scabies: ['mites', 'itch mites'],
  lice: ['head lice', 'nits', 'headlice'],
  'dandruff care': ['dandruff', 'flaky scalp', 'itchy scalp'],
  'ingrown nail treatment': ['ingrown toenail', 'ingrown nail'],
  'ingrown hair treatment': ['ingrown hair', 'razor bumps', 'shaving bumps'],
  'hair loss': ['balding', 'baldness', 'thinning hair', 'alopecia', 'male pattern baldness', 'hairloss'],
  'eyelash growth': ['eyelash', 'lash growth', 'longer lashes'],
  'tick bite treatment': ['tick bite', 'tick', 'lyme', 'lyme disease'],
  'quit smoking': ['smoking cessation', 'stop smoking', 'nicotine', 'vaping'],
  'prescription refills': ['refill', 'refills', 'prescription refill', 'repeat prescription', 'renew prescription'],
  'urgent medical care': ['urgent care', 'walk in', 'see a doctor'],
  gout: ['gouty', 'big toe pain'],

  // Blood pressure: amlodipine is on the formulary, so these are real
  // matches rather than guesses. 105 searches for "blood pressure" were
  // landing on Albuterol.
  'amlodipine prescription': ['blood pressure', 'high blood pressure', 'hypertension', 'bp meds', 'amlodipine', 'norvasc'],

  // Brand to generic. Factual pairs, not clinical decisions.
  sildenafil: ['viagra'],
  tadalafil: ['cialis'],
  'valacyclovir prescription': ['valtrex', 'valacyclovir'],
  'azithromycin prescription': ['zpack', 'z pack', 'z-pack', 'zithromax', 'azithromycin'],
  'amoxicillin prescription': ['amox', 'amoxil', 'amoxicillin'],
  'penicillin prescription': ['penicillin'],
  'albuterol prescription': ['ventolin', 'salbutamol', 'rescue inhaler', 'albuterol'],
  nitrofurantoin: ['macrobid', 'macrodantin'],
  'trimethoprim-sulfamethoxazole': ['bactrim', 'septra', 'co-trimoxazole', 'tmp smx'],
  fluconazole: ['diflucan'],
  famotidine: ['pepcid'],
  benzonatate: ['tessalon'],
  nasonex: ['mometasone', 'nasal spray'],
  'tamiflu prescription': ['oseltamivir', 'tamiflu'],
  doxycycline: ['doxy'],
  doxypep: ['doxy pep', 'doxy-pep'],
};

/**
 * Searched for often, but not on the formulary. Matching these to the
 * nearest-scoring condition is worse than saying so plainly, which is what
 * the loose threshold used to do.
 */
export const UNTREATED_TERMS = [
  'weight loss', 'ozempic', 'semaglutide', 'wegovy', 'mounjaro', 'tirzepatide',
  'adhd', 'adderall', 'ritalin', 'vyvanse',
  'anxiety', 'depression', 'mental health', 'antidepressant', 'xanax',
  'insomnia', 'sleeping pills', 'ambien',
  'diabetes', 'metformin', 'insulin',
  'migraine', 'back pain', 'chronic pain', 'opioid', 'adderal',
  'pneumonia', 'covid', 'birth control', 'testosterone',
];

/**
 * Tighter than the library default of 0.6, which was the direct cause of
 * the mismatches above. ignoreLocation matters because the useful word is
 * often not at the start of the title ("Upper Respiratory Infection").
 */
export const SEARCH_FUSE_OPTIONS = {
  keys: ['title', 'aliases'],
  threshold: 0.3,
  ignoreLocation: true,
  minMatchCharLength: 2,
  shouldSort: true,
};

/**
 * Attaches the alias list to each condition so Fuse can match on it. Items
 * without aliases are returned unchanged apart from an empty array, which
 * keeps the Fuse key shape consistent.
 */
const EMPTY_ITEMS = [];
const aliasCache = new WeakMap();

export function withSearchAliases(conditions) {
  if (!Array.isArray(conditions)) return EMPTY_ITEMS;

  // Callers pass this straight into the search widget's `items` prop during
  // render. Returning a fresh array every time would hand the widget a new
  // identity on each parent render, which re-runs its [items] effect and
  // rebuilds the Fuse index for nothing. Cache per source array so a
  // re-render with the same conditions gets the same reference back.
  const cached = aliasCache.get(conditions);
  if (cached) return cached;

  const mapped = conditions.map((condition) => ({
    ...condition,
    aliases: CONDITION_ALIASES[normaliseTitle(condition?.title)] ?? [],
  }));
  aliasCache.set(conditions, mapped);
  return mapped;
}

/** True when the term is something we knowingly do not treat. */
export function isUntreatedTerm(term) {
  const t = normaliseTitle(term);
  if (!t) return false;
  return UNTREATED_TERMS.some((u) => t === u || t.includes(u));
}

/**
 * Decides where a submitted search should go. Returns null rather than
 * guessing, so callers can tell the patient we do not cover it instead of
 * dropping them on an unrelated condition.
 */
export function resolveSearch(term, results) {
  if (!term || !Array.isArray(results) || results.length === 0) return null;
  if (isUntreatedTerm(term)) return null;
  return results[0];
}
